import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Popper, TextField, TextFieldProps } from '@mui/material';

export type DataTableAutocompleteOption = { id?: string; key: string; value?: string };

const normalizeRegex = /[^a-z0-9]/gi;

type DataTableAutocompleteProps = TextFieldProps & {
    options: DataTableAutocompleteOption[];
    selected?: boolean;
    readonly?: boolean;
    alignRight?: boolean;
    onSelectValue?: (value: DataTableAutocompleteOption | null) => void;
    selectedValue?: DataTableAutocompleteOption | null;
    width?: string | number;
    // Once a value has been chosen, ignore anything that would clear it back to empty -
    // the user can only switch to another option. For fields where "no value" is not a
    // legal state, such as the batch of a batch-tracked material.
    disableClearable?: boolean;
};

const getBackgroundColor = (props: DataTableAutocompleteProps): string => {
    if (props.error) return '#fdecea';
    if (props.readonly) return '#f7f7f7';
    if (props.selected) return '#e9f5f3';
    return 'transparent';
};

const DataTableAutocomplete: FC<DataTableAutocompleteProps> = (props) => {
    const {
        options,
        selected,
        readonly,
        alignRight,
        onSelectValue,
        selectedValue,
        width,
        disabled,
        error,
        disableClearable,
        ...propsToTextField
    } = props;
    const [selectedItem, setSelectedItem] = useState<DataTableAutocompleteOption | null>(null);

    useEffect(() => {
        setSelectedItem(selectedValue || null);
    }, [selectedValue]);

    const styleProps = {
        '& .MuiInputBase-root': {
            padding: 0,
            fontSize: '0.9rem',
            backgroundColor: getBackgroundColor(props),
        },
        border: 'none',
        padding: '0 !important',
        margin: '0 !important',
        '& .MuiOutlinedInput-root': {
            padding: '0 !important',
            margin: '0 !important',
            '& fieldset': {
                border: 'none',
            },
            fontSize: '0.9rem',
        },
        ...props.sx,
    };

    function handleChange(value: DataTableAutocompleteOption | null): void {
        // Guard here rather than only hiding the clear button, so no interaction that
        // produces an empty value - keyboard, escape, blur - can get past it.
        if (disableClearable && !value && selectedItem) {
            return;
        }
        if (value?.key !== selectedItem?.key) {
            setSelectedItem(value);
            if (onSelectValue) {
                onSelectValue(value);
            }
        }
    }

    const normalize = (str: string) => {
        return str.toLowerCase().replace(normalizeRegex, '');
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();

            // find the closest table
            const table = (event.currentTarget as HTMLElement).closest('table');
            if (!table) return;

            // get all inputs, text-areas, selects inside the table
            const focusable = Array.from(
                table.querySelectorAll<HTMLElement>("input, select, textarea, [tabindex]:not([tabindex='-1'])"),
            ).filter((el) => !el.hasAttribute('disabled'));

            // find current active element index
            const index = focusable.indexOf(document.activeElement as HTMLElement);
            const next = focusable[index + 1];

            if (next) {
                next.focus();
            }
        }
    };

    return (
        <Autocomplete
            value={selectedItem}
            disabled={disabled || readonly}
            PopperComponent={(props) => (
                <Popper
                    {...props}
                    placement="bottom-start"
                    style={{
                        ...props.style,
                        width: width,
                        minWidth: width,
                        fontSize: '0.2rem',
                    }}
                />
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    sx={styleProps}
                    {...propsToTextField}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
            filterOptions={(options, state) => {
                const inputValue = normalize(state.inputValue);
                return options.filter((option) => {
                    return (
                        normalize(option.key).includes(inputValue) || normalize(option.value || '').includes(inputValue)
                    );
                });
            }}
            onKeyUp={handleKeyUp}
            getOptionLabel={(option: DataTableAutocompleteOption) => option?.key || option?.value || ''}
            renderOption={(props, option) => (
                <li
                    style={{ fontSize: '0.9rem' }}
                    {...{ ...props, key: `${option?.id}-${option?.key}-${option?.value}` }}
                >
                    {option.value ? `${option?.key} - ${option?.value}` : option?.key}
                </li>
            )}
            options={options || []}
            isOptionEqualToValue={(option, value) => option.id == value.id && option.key === value.key}
            onChange={(_, value) => handleChange(value)}
            sx={{ '& .MuiAutocomplete-clearIndicator': { display: 'none' } }}
            autoHighlight
        />
    );
};

export default DataTableAutocomplete;
